/**
 * POLIVALENCIAS — MAESTRO GLOBAL
 *
 * FASE 3.2B-iv-b. Catálogo global (código/descripción/meses de validez/
 * activo), sin ámbito de cliente ni de centro: quien tiene permiso lo ve y
 * lo gestiona entero. El código es inmutable tras el alta, igual que en el
 * resto de maestros del catálogo (clientes, centros, tipos de limpieza):
 * se muestra en un campo deshabilitado en la edición.
 *
 * SIN BORRADO FÍSICO — desactivar es el mismo PATCH de `activo`. El
 * histórico de evaluaciones y los requisitos de tipo de limpieza que ya la
 * usan siguen intactos (lo garantiza el backend); esta pantalla no vuelve a
 * comprobar eso, sólo ofrece el interruptor y lo explica.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { api, FalloApi, type Polivalencia } from '../api.ts';
import {
  alternarOrden,
  CabeceraOrdenable,
  CampoTexto,
  Error as ErrorCaja,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

type FiltroActivo = 'ACTIVAS' | 'INACTIVAS' | 'TODAS';
type OrdenPolivalencias = 'codigo' | 'descripcion' | 'vigencia' | 'estado';
type Dir = 'asc' | 'desc';

/**
 * `estado: 'ACTIVAS'` — no 'TODAS' — porque es lo que la pantalla ya hacía
 * antes de esta fase: el catálogo esconde por defecto las polivalencias
 * retiradas. Migrar a URL no cambia esa semántica. `codigo ASC` sí es el
 * default nuevo de orden, pero coincide con el `ORDER BY codigo` que ya
 * aplicaba el backend — visualmente nada cambia si no se toca nada.
 */
const DEFAULTS_POLIVALENCIAS = { q: '', estado: 'ACTIVAS', orden: 'codigo', dir: 'asc' } as const;

/**
 * Vigencia no es un número simple: `null` ("No caduca") tiene que caer en
 * un extremo determinista, no donde lo deje `sort()` al comparar `null`
 * con un número. Se representa como -1 —ningún `mesesValidez` real es
 * negativo, `parseMesesValidez` ya lo exige positivo— así "No caduca"
 * siempre queda antes que cualquier caducidad real en ASC, y el ASC/DESC
 * se comporta exactamente igual que en el resto de columnas: invertir la
 * dirección invierte el orden entero, "No caduca" incluido.
 */
function claveVigencia(p: Polivalencia): number {
  return p.mesesValidez === null ? -1 : p.mesesValidez;
}

export function PaginaPolivalencias(): ReactNode {
  const { puede } = useSesion();
  const polivalencias = useDatos(
    () => api.get<{ polivalencias: Polivalencia[] }>('/api/polivalencias'),
    [],
  );

  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [editar, setEditar] = useState<Polivalencia | null>(null);
  const lista = useListaUrl(DEFAULTS_POLIVALENCIAS);

  const q = lista.valor('q');
  const filtro = lista.valor('estado') as FiltroActivo;
  const orden = lista.valor('orden') as OrdenPolivalencias;
  const dir = lista.valor('dir') as Dir;

  function ordenarPor(columna: OrdenPolivalencias): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`.
  const contadorFiltros = filtro !== DEFAULTS_POLIVALENCIAS.estado ? 1 : 0;

  function abrirNuevo() {
    setMostrarNuevo(true);
    setEditar(null);
  }

  function abrirEdicion(p: Polivalencia) {
    setEditar(p);
    setMostrarNuevo(false);
  }

  function cerrarFormulario() {
    setMostrarNuevo(false);
    setEditar(null);
  }

  return (
    <Shell
      titulo="Polivalencias"
      acciones={
        puede('polivalencias:crear') && !mostrarNuevo && editar === null ? (
          <button className="principal" onClick={abrirNuevo}>
            Nueva polivalencia
          </button>
        ) : mostrarNuevo || editar !== null ? (
          <button onClick={cerrarFormulario}>Cancelar</button>
        ) : undefined
      }
    >
      {mostrarNuevo && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Nueva polivalencia</h2>
          </div>
          <FormNuevaPolivalencia
            alCrear={() => {
              cerrarFormulario();
              polivalencias.recargar();
            }}
            alCancelar={cerrarFormulario}
          />
        </div>
      )}

      {editar !== null && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Editar polivalencia</h2>
          </div>
          <FormEditarPolivalencia
            datos={editar}
            alEditar={() => {
              cerrarFormulario();
              polivalencias.recargar();
            }}
            alCancelar={cerrarFormulario}
          />
        </div>
      )}

      <div className="panel">
        <Panel estado={polivalencias}>
          {(d) => {
            const t = q.trim().toLowerCase();
            const filtradas = d.polivalencias.filter((p) => {
              if (filtro === 'ACTIVAS' && !p.activo) return false;
              if (filtro === 'INACTIVAS' && p.activo) return false;
              if (t === '') return true;
              return p.codigo.toLowerCase().includes(t) || p.descripcion.toLowerCase().includes(t);
            });
            const factor = dir === 'asc' ? 1 : -1;
            const ordenadas = [...filtradas].sort((a, b) => {
              if (orden === 'descripcion') return factor * a.descripcion.localeCompare(b.descripcion);
              if (orden === 'vigencia') return factor * (claveVigencia(a) - claveVigencia(b));
              if (orden === 'estado') return factor * (Number(a.activo) - Number(b.activo));
              return factor * a.codigo.localeCompare(b.codigo);
            });

            return (
              <>
                <FiltrosResponsive
                  contador={contadorFiltros}
                  busqueda={
                    <div className="campo campo-busqueda">
                      <label htmlFor="buscar-polivalencia">Buscar</label>
                      <input
                        id="buscar-polivalencia"
                        type="text"
                        placeholder="Código o descripción…"
                        value={q}
                        onChange={(e) => lista.fijar('q', e.target.value)}
                      />
                    </div>
                  }
                >
                  <div className="campo">
                    <label htmlFor="filtro-activo-polivalencia">Estado</label>
                    <select
                      id="filtro-activo-polivalencia"
                      value={filtro}
                      onChange={(e) => lista.fijar('estado', e.target.value)}
                    >
                      <option value="ACTIVAS">Activas</option>
                      <option value="INACTIVAS">Inactivas</option>
                      <option value="TODAS">Todas</option>
                    </select>
                  </div>
                  {!lista.esPorDefecto && (
                    <button
                      type="button"
                      className="pequeno"
                      style={{ alignSelf: 'flex-end' }}
                      onClick={lista.limpiar}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </FiltrosResponsive>

                {d.polivalencias.length === 0 ? (
                  <Vacio titulo="No hay polivalencias en el catálogo">
                    {puede('polivalencias:crear') && (
                      <p className="suave">Crea la primera con el botón «Nueva polivalencia».</p>
                    )}
                  </Vacio>
                ) : ordenadas.length === 0 ? (
                  <div className="panel-cuerpo">
                    <p className="suave">No hay polivalencias que coincidan con los filtros.</p>
                  </div>
                ) : (
                  <div className="tabla-desliza">
                    <table>
                      <thead>
                        <tr>
                          <CabeceraOrdenable
                            texto="Código"
                            columna="codigo"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('codigo')}
                          />
                          <CabeceraOrdenable
                            texto="Descripción"
                            columna="descripcion"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('descripcion')}
                          />
                          <CabeceraOrdenable
                            texto="Vigencia"
                            columna="vigencia"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('vigencia')}
                            className="num"
                          />
                          <CabeceraOrdenable
                            texto="Estado"
                            columna="estado"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('estado')}
                          />
                          {puede('polivalencias:editar') && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {ordenadas.map((p) => (
                          <tr key={p.id}>
                            <td className="cod">{p.codigo}</td>
                            <td>{p.descripcion}</td>
                            <td className="num">
                              {p.mesesValidez === null ? 'No caduca' : `${p.mesesValidez} meses`}
                            </td>
                            <td>
                              <span className={`etiqueta ${p.activo ? 'exito' : 'neutra'}`}>
                                {p.activo ? 'ACTIVA' : 'INACTIVA'}
                              </span>
                            </td>
                            {puede('polivalencias:editar') && (
                              <td className="derecha">
                                <button className="pequeno" onClick={() => abrirEdicion(p)}>
                                  Editar
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          }}
        </Panel>
      </div>
    </Shell>
  );
}

// --------------------------------------------------------------- formularios

function FormNuevaPolivalencia({
  alCrear,
  alCancelar,
}: {
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [mesesStr, setMesesStr] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const mesesValidez = parseMesesValidez(mesesStr);
      if (mesesValidez === 'invalida') {
        setCamposMal({
          mesesValidez: 'Debe ser un número entero positivo, o dejarlo en blanco si no caduca.',
        });
        return;
      }
      await api.post<Polivalencia>('/api/polivalencias', { codigo, descripcion, mesesValidez });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto
          label="Código"
          value={codigo}
          onChange={setCodigo}
          error={camposMal['codigo']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Meses de validez"
          value={mesesStr}
          onChange={setMesesStr}
          error={camposMal['mesesValidez']}
          disabled={enviando}
        />
      </div>
      <p className="tenue pequena" style={{ marginTop: 0 }}>
        Deja «Meses de validez» en blanco si esta polivalencia no caduca nunca.
      </p>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear polivalencia'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormEditarPolivalencia({
  datos,
  alEditar,
  alCancelar,
}: {
  datos: Polivalencia;
  alEditar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [descripcion, setDescripcion] = useState(datos.descripcion);
  const [mesesStr, setMesesStr] = useState(datos.mesesValidez?.toString() ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const mesesValidez = parseMesesValidez(mesesStr);
      if (mesesValidez === 'invalida') {
        setCamposMal({
          mesesValidez: 'Debe ser un número entero positivo, o dejarlo en blanco si no caduca.',
        });
        return;
      }
      await api.patch<Polivalencia>(`/api/polivalencias/${datos.id}`, {
        descripcion,
        mesesValidez,
        activo,
      });
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        {/* Código es inmutable: se muestra deshabilitado para que sea visible */}
        <div className="campo">
          <label htmlFor="polivalencia-codigo">Código</label>
          <input id="polivalencia-codigo" type="text" value={datos.codigo} disabled />
        </div>
        <CampoTexto
          label="Descripción"
          value={descripcion}
          onChange={setDescripcion}
          error={camposMal['descripcion']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Meses de validez"
          value={mesesStr}
          onChange={setMesesStr}
          error={camposMal['mesesValidez']}
          disabled={enviando}
        />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(ev) => setActivo(ev.target.checked)}
            disabled={enviando}
          />
          Polivalencia activa
        </label>
        <p className="tenue pequena" style={{ marginTop: 4, marginBottom: 0 }}>
          Desactivar no borra evaluaciones ni requisitos de tipo de limpieza que ya la usan: sólo
          impide evaluaciones y requisitos nuevos. Se puede reactivar cuando haga falta.
        </p>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// --------------------------------------------------------------- utilidades

/**
 * String vacío → null (no caduca). Número entero positivo → ese número.
 * Cualquier otra cosa → 'invalida'.
 */
function parseMesesValidez(str: string): number | null | 'invalida' {
  const s = str.trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isInteger(n) || n <= 0) return 'invalida';
  return n;
}
